/**
 * Builds the body for `PATCH /api/flights/:id` from the edit form's state.
 *
 * A PATCH carries two different intents over one object, and the key is what
 * tells them apart: an ABSENT key means "leave this column alone", a PRESENT
 * key means "set it to this" — including to nothing. Collapsing an emptied
 * field to `undefined` destroys that distinction, because `JSON.stringify`
 * drops the key, so the API never hears about the change: that is why clearing
 * Operator (or Notes) used to save as a no-op.
 *
 * `operator` and `notes` are nullable, so "clear it" is a real intent and has
 * to travel as a present, empty value. The rest of the form maps to NOT NULL
 * columns, where blank is not an intent at all — an emptied one is left out and
 * the stored value stands.
 */
export interface FlightEdit {
  date?: string;
  departure_airport?: string;
  arrival_airport?: string;
  /** Flight time, in hours. `null` on a leg logged before the basis changed. */
  flight_hours?: number | string | null;
  flight_type?: string;
  operator?: string | null;
  notes?: string | null;
}

export function buildFlightPatch(edit: FlightEdit): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (edit.date) body.date = new Date(edit.date).toISOString();
  if (edit.departure_airport) body.departure_airport = String(edit.departure_airport).toUpperCase();
  if (edit.arrival_airport) body.arrival_airport = String(edit.arrival_airport).toUpperCase();
  if (edit.flight_type) body.flight_type = edit.flight_type;
  // No crew-role key: who flew a leg is a row on its manifest, not a column on
  // the flight, so the edit form has nothing to say about it here.
  // Nullable free text: send whatever the field holds, empty included. The API
  // stores an empty one as NULL.
  if (edit.operator !== undefined) body.operator = edit.operator ?? '';
  if (edit.notes !== undefined) body.notes = edit.notes ?? '';
  // A leg is not saved without a flight time. A blank one reaches the API as 0
  // and is refused there rather than silently dropped; the form checks first with
  // `flightTimeProblem` so the owner reads why.
  if (edit.flight_hours !== undefined) body.flight_hours = Number(edit.flight_hours ?? 0);
  return body;
}

/**
 * Why a typed flight time cannot be saved, or `null` if it can. The same rule the
 * API enforces: a leg is not saved without one, and it is more than 0 h and at
 * most 24 h.
 */
export function flightTimeProblem(value: number | string | null | undefined): string | null {
  if (value === null || value === undefined || String(value).trim() === '') {
    return 'Enter the flight time — a leg cannot be saved without one.';
  }
  const hours = Number(value);
  if (!Number.isFinite(hours) || hours <= 0) return 'Flight time must be greater than 0.';
  if (hours > 24) return 'Flight time cannot be more than 24 hours.';
  return null;
}

/**
 * Ask for a co-owner leg's flight time when it is classified, with FlightAware's
 * airborne figure as the prefill. `undefined` means he cancelled; `null` means
 * "record it without one for now" (an empty answer). Re-asks until the answer is
 * empty or a figure `flightTimeProblem` accepts.
 */
export function askFlightTime(
  airborne: number | string | null | undefined,
  ask: (message: string, initial: string) => string | null = (m, i) => window.prompt(m, i),
): number | null | undefined {
  const measured = Number(airborne) > 0 ? String(Number(airborne)) : '';
  let message =
    "The co-owner's flight time for this leg, in hours." +
    (measured ? ` FlightAware measured ${measured} h airborne.` : '') +
    ' Leave it empty to enter it later.';
  for (;;) {
    const answer = ask(message, measured);
    if (answer === null) return undefined;
    if (answer.trim() === '') return null;
    const problem = flightTimeProblem(answer);
    if (!problem) return Number(answer);
    message = `${problem} The co-owner's flight time, in hours — or leave it empty.`;
  }
}
